import React, { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Route, Boxes, NotebookPen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ENTITY_LABELS, ENTITY_ORDER } from "@/lib/entityColors";
import { useAuth } from "@/context/AuthContext";
import { ResearchExtractDialog } from "./ResearchExtractDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

// Save an AI research answer as a note — attached to an entity, a path, or unscoped ("Research notes").
export const SaveResearchNoteDialog = ({ open, onOpenChange, text, sources }) => {
  const { user } = useAuth();
  const isCurator = user && (user.role === "curator" || user.role === "admin");
  const [browse, setBrowse] = useState(null);
  const [mode, setMode] = useState("general"); // "entity" | "path" | "general"
  const [trackId, setTrackId] = useState("");
  const [node, setNode] = useState(null);
  const [picker, setPicker] = useState(false);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [extractOpen, setExtractOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const srcLines = (sources || [])
      .map((s) => `- ${s.title || s.url}${s.url && s.title ? ` (${s.url})` : ""}`)
      .join("\n");
    setBody(srcLines ? `${text}\n\nSources:\n${srcLines}` : text || "");
    setMode("general"); setTrackId(""); setNode(null);
    if (!browse) api.get("/kg/browse").then((res) => setBrowse(res.data)).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const paths = (browse || []).find((g) => g.type === "knowledge_system")?.nodes || [];
  const allNodes = (browse || []).flatMap((g) => g.nodes.map((n) => ({ ...n, type: g.type })));

  const save = async () => {
    if (mode === "entity" && !node) { toast.error("Pick an entity first"); return; }
    if (mode === "path" && !trackId) { toast.error("Pick a path first"); return; }
    if (!body.trim()) { toast.error("Nothing to save"); return; }
    setSaving(true);
    try {
      await api.post("/notes", {
        entry_type: "note",
        text: body.slice(0, 5000),
        node_id: mode === "entity" ? node.id : null,
        track_id: mode === "path" ? trackId : null,
      });
      toast.success("Research saved to your notes");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const tab = (key, label, Icon) => (
    <button onClick={() => setMode(key)} data-testid={`save-note-mode-${key}`}
      className={`flex items-center justify-center gap-2 text-sm rounded-md border py-2 transition-colors ${
        mode === key ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
      }`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="save-research-note-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <NotebookPen className="w-5 h-5 text-primary" /> Save research as a note
          </DialogTitle>
          <DialogDescription>Choose where to file it, then tidy the note before saving.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {tab("general", "General", NotebookPen)}
            {tab("entity", "An entity", Boxes)}
            {tab("path", "A path", Route)}
          </div>

          {mode === "path" && (
            <Select value={trackId} onValueChange={setTrackId}>
              <SelectTrigger data-testid="save-note-path-select"><SelectValue placeholder="Select a path…" /></SelectTrigger>
              <SelectContent>
                {paths.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {mode === "entity" && (
            <Popover open={picker} onOpenChange={setPicker}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="save-note-entity-trigger">
                  <span className="truncate">{node ? node.label : "Search any entity…"}</span>
                  <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search entities…" data-testid="save-note-entity-search" />
                  <CommandList>
                    <CommandEmpty>No entity found.</CommandEmpty>
                    {ENTITY_ORDER.map((t) => {
                      const items = allNodes.filter((n) => n.type === t);
                      if (items.length === 0) return null;
                      return (
                        <CommandGroup key={t} heading={ENTITY_LABELS[t] || t}>
                          {items.map((n) => (
                            <CommandItem key={n.id} value={`${n.label} ${n.id}`}
                              onSelect={() => { setNode({ id: n.id, label: n.label }); setPicker(false); }}
                              data-testid={`save-note-entity-${n.id}`}>
                              <Check className={`w-4 h-4 mr-2 ${node?.id === n.id ? "opacity-100" : "opacity-0"}`} />
                              {n.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}

          <Textarea value={body} onChange={(e) => setBody(e.target.value)}
            className="min-h-[160px] text-sm" data-testid="save-note-body" />
        </div>

        <DialogFooter>
          {isCurator && (
            <Button variant="outline" onClick={() => setExtractOpen(true)} className="mr-auto" data-testid="save-note-extract">
              <Boxes className="w-4 h-4 mr-1.5" /> Extract entities
            </Button>
          )}
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-note-confirm">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Save note
          </Button>
        </DialogFooter>
      </DialogContent>

      {extractOpen && (
        <ResearchExtractDialog
          open={extractOpen}
          onOpenChange={setExtractOpen}
          text={text}
          sources={sources}
          onCommitted={() => { setExtractOpen(false); onOpenChange(false); }}
        />
      )}
    </Dialog>
  );
};
